import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Field as FormikField } from 'formik';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import {
  ACTION_RESOURCE_BASIS_VALUES,
  ACTION_RESOURCE_FREQUENCY_VALUES,
  ACTION_RESOURCE_STAFF_TYPE_VALUES,
  ACTION_RESOURCE_STAFF_DAY_TYPE_VALUES,
  ACTION_RESOURCE_TYPES,
} from 'src/components/CapActionResource/constants';
import { CapActionDeletePopup } from 'src/components/CapActionDeletePopup/CapActionDeletePopup';
import {
  CapActionResourcePropTypes,
  CapActionResourceDefaultPropTypes,
} from 'src/components/CapActionResource/propTypes';
import CapActionResourceModel, {
  CapActionResourceObjectFactory,
} from 'src/components/CapActionResource/CapActionResourceModel.class';
import { notification } from 'src/components/Notification';
import { useCapActionResourceDelete, useCapActionResourceUpsert } from 'src/hooks/cap';
import { withJobContext } from 'src/utils/context/JobContext';
import authToken from 'src/utils/auth';
import Button from 'src/components/Button';
import CapActionStyle from 'src/components/CapAction/CapAction.styles';
import Loader from 'src/components/Loader';
import logger from 'src/utils/logger';
import SelectBox, { AsyncSelectBox } from 'src/components/SelectBox';
import Style from 'src/components/CapActionResource/CapActionResource.styles';
import { useCurrenciesLazy } from 'src/hooks/currency';

const CapActionResource = (props) => {

  const {
    action,
    allExternalUsersId,
    index,
    isActionEditable,
    isAdmin,
    isExternalUser,
    isFindingEditable,
    isInternalResourceEditorUser,
    jobContext,
    refetchCapHistories,
    removeResourceFromList,
    resource,
    setResourceCache,
  } = props;

  const initialValues = resource.getData();
  const [isEditing, setIsEditing] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const { t } = useTranslation();
  const form = useRef();
  const resourceRef = useRef(null);

  useEffect(() => {
    if (form.current) {
      form.current.getFormikActions().validateForm();
    }
    if(resource.scrollToResouce){
      resourceRef.current.scrollIntoView({behavior: "smooth" , block:"end" , inline:"nearest"})
      resource.scrollToResouce = false;
    }
  });
  const authenticated = authToken();
  const { jobStakeholders } = jobContext;
  const currentUserId = authenticated?.user?.id ? parseInt(authenticated.user.id, 10) : null;
  const canInternalUsersEdit = !!(jobStakeholders?.find(stakeHolder => stakeHolder.userId === currentUserId));
  const canExternalUserDelete = isActionEditable && isExternalUser && allExternalUsersId.includes(currentUserId);

  const updateReadOnlyStatus = () => {

    if (isAdmin) {
      setReadOnly(!isActionEditable);
    } else if (isInternalResourceEditorUser) {
      if (canInternalUsersEdit) {
        setReadOnly(!isActionEditable);
      }
    } else if (isExternalUser && allExternalUsersId.includes(currentUserId)) {
      setReadOnly(!isActionEditable);
    } else {
      setReadOnly(true);
    }
  };

  useEffect(() => {
    updateReadOnlyStatus();
  }, [isAdmin, isInternalResourceEditorUser, isExternalUser, allExternalUsersId, isActionEditable, isFindingEditable]);

  const upsertActionResource = useCapActionResourceUpsert();
  const deleteActionResource = useCapActionResourceDelete();

  const frequencyOptions = Object
    .entries(ACTION_RESOURCE_FREQUENCY_VALUES)
    .map(v => ({ value: v[1], label: v[1] }));

  const staffTypeOptions = Object
    .entries(ACTION_RESOURCE_STAFF_TYPE_VALUES)
    .map(v => ({ value: v[1], label: v[1] }));

  const staffDayTypeOptions = Object
    .entries(ACTION_RESOURCE_STAFF_DAY_TYPE_VALUES)
    .map(v => ({ value: v[1], label: v[1] }));

  const basisOptions = Object
    .entries(ACTION_RESOURCE_BASIS_VALUES)
    .map(v => ({ value: v[1], label: v[1] }));

  const [loadCurrenciesLazy, { data}] = useCurrenciesLazy({
    limit: 1000,
    offset: 0,
  });

  useEffect(() => {
    loadCurrenciesLazy();
  },[]);

  const currencies = ((data && data.currencies) || []);

  const currencyOptions = useMemo(() => sortBy(currencies, ['code'])
    .map(currency => ({ value: currency.id, label: `${currency.code}(${currency.name})` })),
  [currencies]);

  const onCancelClicked = (formProps) => {
    if (!resource.isNew() && isEditing) {
      setIsEditing(false);
      resource.reInit(initialValues);
      formProps.resetForm();
    }

    if (resource.isNew()) {
      removeResourceFromList(resource.id);
    }
  };

  const isActionVirtual = action && !action.id;
  const renderResourceForm = (formProps) => {

    if (resource.staffDayType === null) {
      resource.staffDayType = 'Days';
      formProps.setFieldValue('staffDayType', 'Days');
    }

    return (
      <Style.ResourceForm ref={resourceRef} onSubmit={formProps.handleSubmit}>
        <Style.LeftColumn>
          <Style.ActionResourceHintTextDiv>
            {index === 0 &&
              <Style.ActionResourceHintText lang={i18n.language}>{t('RESOURCES')}</Style.ActionResourceHintText>}
          </Style.ActionResourceHintTextDiv>
          <Style.LeftColumnRows>
            {!resource.frequency && resource.resourceType === ACTION_RESOURCE_TYPES.NO_RESOURCE &&
            <Style.DivRow>
              <Style.RadioGroupItemRow>
                <Style.RadioGroupItem>
                  <Style.RadioGroupInput
                    checked={resource.isVirtualType()}
                    disabled={isEditing ? false : !resource.isNew() || !isActionEditable}
                    name="resourceType"
                    onChange={(e) => {
                      resource.reInit({ id: resource.id, actionId: resource.actionId });
                      resource.resourceType = ACTION_RESOURCE_TYPES.NO_RESOURCE;
                      formProps.resetForm({ ...resource.getData() });
                      formProps.handleChange(e);
                    }}
                    type="radio"
                    value={ACTION_RESOURCE_TYPES.NO_RESOURCE}
                  />
                  <Style.RadioGroupCircle />
                  <Style.RadioGroupText>{t('NO_RESOURCE')}</Style.RadioGroupText>
                </Style.RadioGroupItem>
              </Style.RadioGroupItemRow>
            </Style.DivRow>
            }
            <Style.DivRow>
              <Style.RadioGroupItemRow>
                <Style.RadioGroupItem>
                  <Style.RadioGroupInput
                    checked={resource.isInvestmentType()}
                    disabled={isEditing ? false : (!resource.isNew() || isActionVirtual || !isActionEditable)}
                    name="resourceType"
                    onChange={(e) => {
                      resource.reInit({ id: resource.id, actionId: resource.actionId });
                      resource.resourceType = ACTION_RESOURCE_TYPES.INVESTMENT;
                      formProps.resetForm({ ...resource.getData() });
                      formProps.handleChange(e);
                    }}
                    type="radio"
                    value={ACTION_RESOURCE_TYPES.INVESTMENT}
                  />
                  <Style.RadioGroupCircle />
                  <Style.RadioGroupText>{t('INVESTMENT')}</Style.RadioGroupText>
                </Style.RadioGroupItem>
              </Style.RadioGroupItemRow>
            </Style.DivRow>
            <Style.DivRow>
              <Style.RadioGroupItemRow>
                <Style.RadioGroupItem>
                  <Style.RadioGroupInput
                    checked={resource.isStaffDaysType()}
                    disabled={isEditing ? false : (!resource.isNew() || isActionVirtual || !isActionEditable)}
                    name="resourceType"
                    onChange={(e) => {
                      resource.reInit({ id: resource.id, actionId: resource.actionId });
                      resource.resourceType = ACTION_RESOURCE_TYPES.NUMBER_OFF_STAFF_DAYS;
                      formProps.resetForm({ ...resource.getData() });
                      formProps.handleChange(e);
                    }}
                    type="radio"
                    value={ACTION_RESOURCE_TYPES.NUMBER_OFF_STAFF_DAYS}
                  />
                  <Style.RadioGroupCircle />
                  <Style.RadioGroupText>{t('NUMBER_OFF_STAFF')} {resource.staffDayType ? resource.staffDayType.toLowerCase() : 'days'}</Style.RadioGroupText>
                </Style.RadioGroupItem>
              </Style.RadioGroupItemRow>
            </Style.DivRow>

            {!readOnly && !resource.isVirtualType() &&
              <Style.DivRow>
                {/* eslint-disable-next-line no-nested-ternary */}
                {resource.isNew() || isEditing ?
                  (formProps.isSubmitting ? <Loader withText={false} />
                    :
                    <>
                      <Style.SaveButton solid orange disabled={Object.keys(formProps.errors).length > 0}
                                        type="submit" onClick={(e) => {
                                          e.preventDefault();
                                          formProps.handleSubmit(e);
                                        }}>{t('SAVE')}</Style.SaveButton>
                      <Button solid orange onClick={() => onCancelClicked(formProps)} type="button">{t('CANCEL')}</Button>
                    </>)
                  : <Button solid orange onClick={(e) => {
                    formProps.setSubmitting(false);
                    e.preventDefault();
                    setIsEditing(true);
                  }} type="button">{t('EDIT')}</Button>}
              </Style.DivRow>
            }
          </Style.LeftColumnRows>
        </Style.LeftColumn>

        <Style.CenterColumn>
          {resource.isVirtualType() &&
            <Style.DivRow>
              {t('NO_NECESSARY_RESOURCE_TEXT')}
            </Style.DivRow>
          }

          {!resource.isVirtualType() &&
          <Style.DivRow>
            <FormikField
              name="frequency"
              validate={CapActionResourceModel.ValidateDetails(resource)}>
              {() => (
                <SelectBox
                  disabled={isEditing ? false : !resource.isNew()}
                  error={formProps.errors.frequency}
                  label={t('FREQUENCY')}
                  name="frequency"
                  onChange={(selectedOption) => {
                    resource.frequency = selectedOption.value;
                    formProps.setFieldValue('frequency', selectedOption.value);
                    if (resource.isOneTime()) {
                      formProps.setValues({
                        ...resource.getData(),
                        basis: null,
                        frequency: selectedOption.value
                      });
                    }
                  }}
                  options={frequencyOptions}
                  required={true}
                  value={frequencyOptions.find(frequencyOption => frequencyOption.value === resource.frequency) || null} />  
              )}
            </FormikField>
          </Style.DivRow>
          }
          {resource.isStaffDaysType() &&
            <Style.DivRow half={true} >
              <FormikField
                name="staffDayType"
                validate={CapActionResourceModel.ValidateStaffDayType(resource)}>
                {() => (
                  <SelectBox
                    disabled={isEditing ? false : !resource.isNew()}
                    error={formProps.errors.staffDayType}
                    label={t('NUMBER_OF')}
                    name="staffDayType"
                    onChange={(selectedOption) => {
                      resource.staffDayType = selectedOption.value;
                      formProps.setFieldValue('staffDayType', selectedOption.value);
                    }}
                    options={staffDayTypeOptions}
                    required={true}
                    value={staffDayTypeOptions.find(staffDayTypeOption => staffDayTypeOption.value === resource.staffDayType) || null}
                  />
                )}
              </FormikField>
              <FormikField name="staffDayValue"
                           validate={CapActionResourceModel.ValidateStaffDayValue(resource)}>
                {() => (
                  <Style.FormTextField
                    disabled={isEditing ? false : !resource.isNew()}
                    error={formProps.errors.staffDayValue}
                    grayedOut={isEditing ? false : !resource.isNew()}
                    label={t('VALUE')}
                    name="staffDayValue"
                    onChange={(e) => {
                      resource.staffDayValue = e.target.value;
                      formProps.handleChange(e);
                    }}
                    required={true}
                    value={resource.staffDayValue} />
                )}
              </FormikField>
            </Style.DivRow>
          }
          {resource.isStaffDaysType() &&
            <Style.DivRow>
              <FormikField
                name="staffType"
                validate={CapActionResourceModel.ValidateStaffDayType(resource)}>
                {() => (
                  <SelectBox
                    disabled={isEditing ? false : !resource.isNew()}
                    error={formProps.errors.staffType}
                    label={t('STAFF_TYPE')}
                    name="staffType"
                    onChange={(selectedOption) => {
                      resource.staffType = selectedOption.value;
                      formProps.setFieldValue('staffType', selectedOption.value);
                    }}
                    options={staffTypeOptions}
                    required={true}
                    value={staffTypeOptions.find(staffTypeOption => staffTypeOption.value === resource.staffType) || null} />
                )}

              </FormikField>
            </Style.DivRow>
          }
          {!resource.isVirtualType() &&
          <>
          <Style.DivRow>
            <FormikField name="currencyId" validate={CapActionResourceModel.ValidateCurrency(resource)}>
              {() => (
                <AsyncSelectBox
                  disabled={isEditing ? false : !resource.isNew()}
                  error={formProps.errors.currencyId}
                  isClearable={resource.isStaffDaysType()}
                  label={t('CURRENCY')}
                  name="currencyId"
                  onChange={(selectedOption) => {
                    resource.currencyId = selectedOption?.value || null;
                    formProps.setFieldValue('currencyId', selectedOption?.value || null);
                  }}
                  onInputChange={newValue => {
                    loadCurrenciesLazy({ variables: { q: newValue } });
                  }}
                  options={currencyOptions}
                  required={resource.isInvestmentType()}
                  value={currencyOptions.find(currency => currency.value === resource.currencyId) || null}
                />
              )}
            </FormikField>
          </Style.DivRow>

          <Style.DivRow>
            <FormikField name="costs" validate={CapActionResourceModel.ValidateCosts(resource)}>
              {() => (
                <Style.FormTextField
                  disabled={isEditing ? false : !resource.isNew()}
                  error={formProps.errors.costs}
                  grayedOut={isEditing ? false : !resource.isNew()}
                  label={t('COSTS')}
                  name="costs"
                  onChange={(e) => {
                    resource.costs = e.target.value;
                    formProps.handleChange(e);
                  }}
                  required={resource.isInvestmentType()}
                  value={resource.costs} />
              )}
            </FormikField>
          </Style.DivRow>

          <Style.DivRow>
            <FormikField name="details" validate={CapActionResourceModel.ValidateDetails(resource)}>
              {() => (
                <Style.FormTextField
                  disabled={isEditing ? false : !resource.isNew()}
                  error={formProps.errors.details}
                  grayedOut={isEditing ? false : !resource.isNew()}
                  label={t('DETAILS')}
                  multiline={true}
                  name="details"
                  onChange={(e) => {
                    resource.details = e.target.value;
                    formProps.handleChange(e);
                  }}
                  required
                  value={resource.details} />
              )}
            </FormikField>
          </Style.DivRow>
          </>
          }
        </Style.CenterColumn>

        <Style.RightColumn>
          {!resource.isNew() && (isAdmin || canInternalUsersEdit || canExternalUserDelete) && (
            <CapActionStyle.RemoveButton
              name="remove"
              onClick={() => setShowDeletePopup(true)}
            />
          )}
          {resource.isRecurring() && (
            <>
              <Style.DivRow />
              {resource.isInvestmentType() && (<Style.DivRow />)}
              <Style.DivRow>
                <FormikField name="basis"
                             validate={CapActionResourceModel.ValidateBasis(resource)}>
                  {() => (
                    <SelectBox
                      disabled={isEditing ? false : !resource.isNew()}
                      error={formProps.errors.basis}
                      label={t('BASIS')}
                      name="basis"
                      onChange={(selectedOption) => {
                        resource.basis = selectedOption.value;
                        formProps.setFieldValue('basis', selectedOption.value);
                      }}
                      options={basisOptions}
                      required={true}
                      value={basisOptions.find(basisOption => basisOption.value === resource.basis) || null}
                    />
                  )}
                </FormikField>
              </Style.DivRow>
            </>
          )}
        </Style.RightColumn>
      </Style.ResourceForm>
    );
  };

  const upsertWithStateUpdate = async (args) => {

    const response = await upsertActionResource(args);
    const newResourceData = response?.data?.CapUpsertActionResource?.actionResource;
    refetchCapHistories();
    setResourceCache(new CapActionResourceModel(CapActionResourceObjectFactory(newResourceData)));
  };

  const persistResource = async (formValues, { setSubmitting }) => {
    if (readOnly) {
      return;
    }
    const requestPayload = CapActionResourceModel.TransformFormDataToRequestData(resource, formValues);
    try {
      await upsertWithStateUpdate(requestPayload);
    } catch (err) {
      logger.error(err);
      if (resource.isNew()) {
        notification.error(t('RESOURCE_ADD_ERROR'));
      } else {
        notification.error(t('RESOURCE_UPDATE_ERROR'));
      }
    } finally {
      setIsEditing(false);
      setSubmitting(false);
    }
  };

  const deleteResource = async (resourceObject) => {
    if (resourceObject.isNew()) {
      removeResourceFromList(resourceObject.id);
      setShowDeletePopup(false);
    } else {
      try {
        const response = await deleteActionResource(resourceObject.id);
        if (!response?.data?.CapDeleteActionResource?.status) {
          notification.error(t('RESOURCE_DELETE_ERROR'));
        } else {
          removeResourceFromList(resourceObject.id);
        }
      } catch (err) {
        logger.error(err);
        notification.error(t('RESOURCE_DELETE_ERROR'));
      } finally {
        setShowDeletePopup(false);
      }
    }
  };

  return (
    <Style.ResourceDiv className={resource.isNew()? 'ignore-in-pdf':''}>
      {showDeletePopup && (
        <CapActionDeletePopup
          onClose={() => setShowDeletePopup(false)}
          onSubmit={() => deleteResource(resource)}
        />
      )}
      <Formik initialValues={{ ...initialValues }}
              onSubmit={persistResource}
              ref={form}
              validateOnChange={true}
              validateOnMount={true}
      >
        {renderResourceForm}
      </Formik>
    </Style.ResourceDiv>);
};

CapActionResource.propTypes = CapActionResourcePropTypes;
CapActionResource.defaaultProps = CapActionResourceDefaultPropTypes;

export default React.memo(withJobContext(CapActionResource));
